import React, { useState, useEffect, useRef } from "react";
import "./AppointmentCalendar.css"; // Import custom styles

const ScrollableCalendar = ({
  selectedDoctor,
  selectedDate,
  setSelectedDate,
  handleDateChange,
  availability,
}) => {
  const today = new Date();
  const [startDate, setStartDate] = useState(new Date()); // Start date for scrolling
  // const [selectedDate, setSelectedDate] = useState(null); // Selected date state
  const [availableTimes, setAvailableTimes] = useState([]); // Times available for the selected date
  const activeCardRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Generate dates within the allowed range (3 days back and 14 days ahead)
  const generateValidDates = () => {
    const validDates = [];
    const start = new Date(today);
    start.setDate(today.getDate() - 3); // 3 days back
    for (let i = 0; i <= 17; i++) {
      // 3 days back + 14 days ahead = 18 days total
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      validDates.push(date);
    }
    return validDates;
  };

  const validDates = generateValidDates();

  const dateToStr = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Ensure two digits
    const day = String(date.getDate()).padStart(2, "0"); // Ensure two digits
    return `${year}-${month}-${day}`; // Return the formatted date
  };

  // Update available times based on selected person and date
  useEffect(() => {
    if (selectedDate && selectedDoctor) {
      const personAvailability = availability[selectedDoctor.value] || {};
      const formattedDate = dateToStr(selectedDate);
      setAvailableTimes(personAvailability[formattedDate] || []);
    }
  }, [selectedDate, selectedDoctor]);

  // Scroll the selected card into view whenever selectedDate changes
  useEffect(() => {
    if (activeCardRef.current) {
      activeCardRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [selectedDate]);

  const handlePrev = () => {
    const prevDate = new Date(startDate);
    prevDate.setDate(startDate.getDate() - (isMobile ? 1 : 5)); // Move back by 5 days
    if (prevDate >= validDates[0]) {
      // Ensure within valid range
      setStartDate(prevDate);
    }
  };

  const handleNext = () => {
    const nextDate = new Date(startDate);
    nextDate.setDate(startDate.getDate() + (isMobile ? 1 : 5)); // Move forward by 5 days
    if (nextDate <= validDates[validDates.length - (isMobile ? 1 : 5)]) {
      // Ensure within valid range
      setStartDate(nextDate);
    }
  };

  const handleSelectDate = (date) => {
    if (date) {
      setSelectedDate(date); // Set the clicked date as selected
      handleDateChange(date); // Trigger the provided callback for date change
    }
  };

  // Determine if a date has available slots
  const hasAvailabilityForPerson = (date) => {
    if (!selectedDoctor) {
      return false;
    }
    const formattedDate = dateToStr(date);
    return availability.includes(formattedDate);
  };

  // Format the date to match the desired format:
  // Weekday abbreviation
  // day, full month name
  // year
  const formatDate = (date) => {
    const weekday = date.toLocaleDateString("pl-PL", { weekday: "short" }); // Abbreviated weekday
    const month = date.toLocaleString("pl-PL", { month: "long" }); // Full month name
    const day = date.getDate(); // Day of the month
    const year = date.getFullYear(); // Year
    return {
      weekday: weekday.charAt(0).toUpperCase() + weekday.slice(1),
      month: month.charAt(0).toUpperCase() + month.slice(1),
      day,
      year,
    };
  };

  return (
    <div className="scrollable-calendar">
      {!isMobile && (
        <button
          onClick={handlePrev}
          className="nav-arrow"
          disabled={startDate <= validDates[0]}
        >
          {"<"}
        </button>
      )}
      <div className="calendar-cards-container">
        {validDates
          .slice(
            validDates.findIndex(
              (d) => d.toDateString() === startDate.toDateString(),
            ),
            validDates.findIndex(
              (d) => d.toDateString() === startDate.toDateString(),
            ) + (isMobile ? validDates.length : 5),
          )
          .map((day, index) => {
            const isSelected =
              selectedDate?.toDateString() === day.toDateString();
            const isAvailable = hasAvailabilityForPerson(day);
            const { weekday, month, day: dayNum, year } = formatDate(day);
            return (
              <div
                key={index}
                ref={isSelected ? activeCardRef : null} // Attach ref only to the active card
                className={`calendar-card ${
                  isSelected ? "selected" : isAvailable ? "available" : ""
                }`}
                // className={`calendar-card ${
                //     selectedDate?.toDateString() === day.toDateString()
                //     ? "selected"
                //     : isAvailable
                //     ? "available"
                //     : ""
                //     }`}
                onClick={() => handleSelectDate(day)}
              >
                <div className="card-header" style={{ fontSize: "18px" }}>
                  {weekday}
                </div>
                <div className="card-body">
                  <p
                    style={{ marginLeft: month === "Październik" ? "-5px" : "0px", 
                      marginTop: "0px", marginBottom: "0px", marginRight:"0px",
                      textAlign:"left", fontSize: month === "Październik" ? "13px" : "14px" }}
                  >
                    {month}
                  </p>
                  <p style={{ fontSize: "28px", margin: "2px" }}>{dayNum}</p>
                  <p style={{ fontSize: "14px" }}>{year}</p>
                </div>
              </div>
            );
          })}
      </div>
      {!isMobile && (
        <button
          onClick={handleNext}
          className="nav-arrow"
          disabled={startDate >= validDates[validDates.length - 5]}
        >
          {">"}
        </button>
      )}
    </div>
  );
};

export default ScrollableCalendar;
